/**
 * Small SVG charts, hand-rolled.
 *
 * The brief allowed "Recharts or similar". Recharts would add roughly as much
 * JavaScript again as the entire app currently ships, on a 2016 iPad with 2GB of
 * RAM, to draw a line and some bars. These are about eighty lines and no
 * dependency. If the charts ever get genuinely complicated, swapping in a real
 * library is a contained change: only this file draws anything.
 *
 * Everything scales to a viewBox, so it stays sharp on the iPad's 2x display and
 * needs no resize handling.
 */

interface Point {
  label: string
  value: number
}

/** Line chart with an optional rolling average behind it. */
export function TrendChart({
  points,
  rolling,
  color,
  height = 120,
  ariaLabel,
}: {
  points: Point[]
  rolling?: number[]
  color: string
  height?: number
  ariaLabel: string
}) {
  if (points.length === 0) return null
  const W = 600
  const H = height
  const PAD = { top: 10, right: 8, bottom: 18, left: 24 }
  const max = Math.max(1, ...points.map((p) => p.value), ...(rolling ?? []))
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const x = (i: number) => PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW)
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH

  const path = (values: number[]) => values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} preserveAspectRatio="none">
      {/* baseline and midline for a sense of scale */}
      <line x1={PAD.left} y1={y(0)} x2={W - PAD.right} y2={y(0)} className="chart__axis" />
      <line x1={PAD.left} y1={y(max)} x2={W - PAD.right} y2={y(max)} className="chart__grid" />
      <text x={2} y={y(max) + 4} className="chart__tick">
        {max}
      </text>

      {rolling && rolling.length > 1 && <path d={path(rolling)} className="chart__rolling" />}
      <path d={path(points.map((p) => p.value))} fill="none" stroke={color} strokeWidth={2.5} />
      {points.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.value)} r={3.5} fill={color} />
      ))}
      {points.map((p, i) =>
        // Only label the ends and every third point, or they collide.
        i === 0 || i === points.length - 1 || i % 3 === 0 ? (
          <text key={'l' + i} x={x(i)} y={H - 4} className="chart__tick" textAnchor="middle">
            {p.label}
          </text>
        ) : null,
      )}
    </svg>
  )
}

/** Horizontal bars, used for comparisons where the labels matter more than precision. */
export function BarRow({
  label,
  value,
  max,
  color,
  suffix = '',
}: {
  label: string
  value: number
  max: number
  color: string
  suffix?: string
}) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 2
  return (
    <div className="bar">
      <span className="bar__label">{label}</span>
      <span className="bar__track">
        <span className="bar__fill" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className="bar__value">
        {value}
        {suffix}
      </span>
    </div>
  )
}

/** Rolling mean over the previous `window` values, inclusive. */
export function rollingAverage(values: number[], window: number): number[] {
  return values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - window + 1), i + 1)
    return Math.round((slice.reduce((a, b) => a + b, 0) / slice.length) * 10) / 10
  })
}
